import { useState } from 'react';
import { useT } from '@hana/i18n';
import './bookingI18n';
import { IconCalendarCheck } from '@tabler/icons-react';
import type { BookingRequest } from './types';
import s from './dock.module.css';

// Bảng phải của Mint-Booking-1920 (Tải BKG · Việc · Thông báo). Số RQ không có tiền. Dữ liệu Tải / Việc / Thông báo là mẫu của board.

type DockTab = 'load' | 'task' | 'noti';
const TABS: ReadonlyArray<{ key: DockTab; label: string }> = [
  { key: 'load', label: 'Tải BKG' },
  { key: 'task', label: 'Việc' },
  { key: 'noti', label: 'Thông báo' },
];

/** Thứ tự + màu chấm trạng thái theo board (Stock không có màu nên không vào biểu đồ). */
const STATUS: ReadonlyArray<{ tab: BookingRequest['tab']; label: string; dot: string }> = [
  { tab: 'queue', label: 'Hàng chờ RQ', dot: 'var(--d-gray)' },
  { tab: 'doing', label: 'Đang làm', dot: 'var(--d-blue)' },
  { tab: 'standby', label: 'Standby', dot: 'var(--d-amber)' },
  { tab: 'amend', label: 'Amend', dot: 'var(--d-violet)' },
  { tab: 'notify', label: 'Cần báo khách', dot: 'var(--warn)' },
  { tab: 'done', label: 'Đã xong', dot: 'var(--d-green)' },
];

const LOAD = [
  { k: 'PHUONGHT', ab: 'PH', v: 518, sub: '146 standby · backup TRANTPB' },
  { k: 'NHITHT', ab: 'NH', v: 337, sub: '48 standby · nghỉ hôm nay, PHUONGHT thay' },
  { k: 'TRANTPB', ab: 'TR', v: 448, sub: '89 standby · backup NHITHT' },
];
const TASKS = [
  { code: 'BKO', k: 'Mở book ONE W45 · hẹn mở hôm nay', due: 'Hạn 17:30 hôm nay', late: false },
  { code: 'VCN', k: 'Báo khách đổi lịch KMTC XIAMEN 2608N', due: 'Trong ngày', late: false },
  { code: 'STK', k: 'Trả chỗ ONE VNVUT → FRFOS trước 23/Oct 12:00', due: 'Còn 27 giờ', late: true },
  { code: 'RQU', k: 'RQ Update · đổi 2x40RH → 3x40RH (SE260638710)', due: 'Quá hạn 1 giờ', late: true },
  { code: 'SBY', k: 'Rà Standby thứ Hai', due: '26/Oct/2026 09:00', late: false },
];
const NOTIS = [
  { k: 'ONE xác nhận booking SGNGCP779500 · SE260623250', when: '09:12', dot: 'var(--d-blue)', unread: true },
  { k: 'WHL báo delay WAN HAI 622 W028 · 2 job', when: '09:05', dot: 'var(--d-amber)', unread: true },
  { k: 'DAVID gửi RQ mới SE260639440 · QUEKYCORP', when: '08:58', dot: 'var(--d-blue)', unread: false },
  { k: 'NHITHT nghỉ hôm nay · tag PIC và thông báo chuyển PHUONGHT', when: '07:30', dot: 'var(--d-gray)', unread: false },
  { k: 'Stock YML W44 còn 2 chỗ chưa gán', when: 'Hôm qua 17:40', dot: 'var(--d-violet)', unread: false },
];

export function Dock({ rows, onPick, onClose }: { rows: readonly BookingRequest[]; onPick: (tab: BookingRequest['tab']) => void; onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<DockTab>('load');
  const st = STATUS.map((x) => ({ ...x, n: rows.filter((r) => r.tab === x.tab).length }));
  const total = st.reduce((a, x) => a + x.n, 0);
  const car = new Map<string, [number, number]>();
  for (const r of rows) {
    if (!r.carrier) continue;
    const m = car.get(r.carrier) ?? [0, 0];
    m[0] += 1;
    m[1] += parseInt(r.cont, 10) || 0;
    car.set(r.carrier, m);
  }
  const top = [...car.entries()].sort((a, b) => b[1][0] - a[1][0]).slice(0, 6);
  const topMax = Math.max(1, ...top.map(([, m]) => m[0]));
  const mx = Math.max(...LOAD.map((l) => l.v));
  const avg = LOAD.reduce((a, l) => a + l.v, 0) / LOAD.length;

  return (
    <aside className={s.dock}>
      <div className={s.head}>
        <div role="tablist" aria-label={t('booking.a11y30')} className={s.tabs}>
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`${s.tab} ${tab === t.key ? s.on : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <button type="button" className={s.close} aria-label={t('booking.a11y31')} onClick={onClose}>
          ×
        </button>
      </div>
      {tab === 'load' && (
        <>
          <section aria-label={t('booking.a11y32')} className={s.card}>
            <div className={s.cardH}>
              <IconCalendarCheck size={18} stroke={1.8} aria-hidden="true" />
              <h2>{t('booking.byStatus')}</h2>
              <span>{total} RQ · theo lọc</span>
            </div>
            <div role="img" aria-label={`Phân bố ${total} RQ theo trạng thái: ${st.map((x) => `${x.label} ${x.n}`).join(', ')}`} className={s.seg}>
              {st.map((x) => (
                <div key={x.tab} style={{ flex: `${x.n} 1 0`, background: x.dot }} />
              ))}
            </div>
            <div className={s.legend}>
              {st.map((x) => (
                <button key={x.tab} type="button" className={s.lg} onClick={() => onPick(x.tab)}>
                  <span className={s.dot} style={{ background: x.dot }} />
                  <span className={s.lgK}>{x.label}</span>
                  <b>{x.n}</b>
                </button>
              ))}
            </div>
          </section>
          <section aria-label={t('booking.a11y33')} className={s.card}>
            <div className={`${s.cardH} ${s.cardHb}`}>
              <h2>{t('booking.byCarrier')}</h2>
              <span>{t('booking.rqCont')}</span>
            </div>
            <div role="img" aria-label={`Số RQ theo hãng: ${top.map(([k, m]) => `${k} ${m[0]}${m[1] ? ` (${m[1]} cont)` : ''}`).join(', ')}`}>
              {top.map(([k, m]) => (
                <div key={k} className={s.bar}>
                  <span className={s.barK}>{k}</span>
                  <div className={s.barT}>
                    <div style={{ width: `${Math.round((m[0] / topMax) * 100)}%` }} />
                  </div>
                  <b>{m[0]}</b>
                  <span className={s.barC}>{m[1] ? `${m[1]} cont` : ''}</span>
                </div>
              ))}
            </div>
          </section>
          <section className={s.card}>
            <div className={s.cardH}>
              <h2>{t('booking.teamLoad')}</h2>
              <span>{t('booking.days90')}</span>
            </div>
            {LOAD.map((l, i) => {
              const dev = Math.round(((l.v - avg) / avg) * 100);
              const over = Math.abs(dev) > 15;
              return (
                <div key={l.k} className={s.ld} style={{ borderTopWidth: i ? 1 : 0 }}>
                  <div className={s.ldH}>
                    <span className={s.ab}>{l.ab}</span>
                    <b>{l.k}</b>
                    <span className={s.dev} style={{ color: over ? 'var(--warn)' : 'var(--lb)' }}>
                      {dev > 0 ? '+' : ''}
                      {dev}%
                    </span>
                  </div>
                  <div className={s.ldB}>
                    <div className={s.barT}>
                      <div style={{ width: `${Math.round((l.v / mx) * 100)}%`, background: over ? 'var(--d-amber)' : undefined }} />
                    </div>
                    <span className={s.ldV}>{l.v}</span>
                  </div>
                  <div className={s.ldS}>{l.sub}</div>
                </div>
              );
            })}
          </section>
        </>
      )}
      {tab === 'task' &&
        TASKS.map((k) => (
          <label key={k.code} className={s.task}>
            <input type="checkbox" aria-label={`Xong: ${k.k}`} />
            <div>
              <div>
                <span className={s.code}>{k.code}</span> {k.k}
              </div>
              <div className={s.due} style={{ color: k.late ? 'var(--err)' : 'var(--lb)' }}>
                {k.due}
              </div>
            </div>
          </label>
        ))}
      {tab === 'noti' &&
        NOTIS.map((n, i) => (
          <div key={n.k} className={s.noti} style={{ borderTopWidth: i ? 1 : 0 }}>
            <span className={s.nDot} style={{ background: n.dot }} />
            <div>
              <div style={{ fontWeight: n.unread ? 600 : 450 }}>{n.k}</div>
              <div className={s.due}>{n.when}</div>
            </div>
          </div>
        ))}
    </aside>
  );
}
